import React, { useCallback, useEffect, useRef } from 'react';
import { BackButton, Input, ResultValue, ErrorAlert, ResultAlert, Button, ErrorMessage, Select } from '../Components/Simple';
import useAsyncPricerApi from '../Hooks/useAsyncPricerApi';
import { ApiError, ProviderMapper, EntryTable, EntryTableColumn, MultiInput, MultiInputType } from '../Components/Combined';
import { API_DATE_STRING_FORMAT, DATE_STRING_FORMAT } from '../Constants/stringFormats';
import { OPPS as modelType } from '../Constants/modelTypes';
import useCalendarYear from '../Hooks/useCalendarYear';
import useSessionState from '../Hooks/useSessionState';
import { useNavigate, useLocation } from 'react-router-dom';
import useCollection from '../Hooks/useCollection';
import useOceApi from '../Hooks/useOceApi';
import useExportRequest from '../Hooks/useExportRequest';
import useImportRequest from '../Hooks/useImportRequest';
import { formatDateStringIfMatches, formatStringAsCurrency, getDefaultDateString } from '../Utils/stringUtils';
import { validateBilled, validateLineServiceDate, validateZipCode } from '../Utils/validation';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import { mapToSetters } from '../Utils/mapping';
import prepareServiceLines from '../Utils/prepareServiceLines';
import { transformNumberToOneDecimalPlace, transformStringToUpCase } from '../Utils/valueTransforms';
import ValidatedInput from '../Components/Combined/ValidatedInput';
import { enable5ZipWarning, enableAsyncPricingApi, enableOceEnhancements } from '../Utils/features';
import OCEResults from '../Components/Results/OCEResults';
import { isNotNullOrUndefined } from '@microsoft/applicationinsights-core-js';
import { genderValues, serviceModeValues } from '../Constants/dropdownValues';
import usePricerApi from '../Hooks/usePricerApi';
import { EXECUTE } from '../Constants/serviceMode';


export default function OPPS() {
    const location = useLocation();
    const { prePopulatedMedicareId, prePopulatedPlaceOfService, prePopulatedNPI, prePopulatedBillType } = location.state || {};
    const [claimId, setClaimId] = useSessionState('', 'claimId');
    const [medicareId, setMedicareId] = useSessionState(prePopulatedMedicareId ?? '', 'medicareId');
    const [providerName, setProviderName] = useSessionState('', 'providerName');
    const [npi, setNpi] = useSessionState(prePopulatedNPI ?? '', 'npi');
    const [placeOfService, setPlaceOfService] = useSessionState(prePopulatedPlaceOfService ?? '', 'placeOfService');
    const [zipCode, setZipCode] = useSessionState('', 'zipCode');
    const [billType, setBillType] = useSessionState(prePopulatedBillType ?? '', 'billType');
    const [fromDate, setFromDate] = useSessionState(getDefaultDateString(), 'fromDate');
    const [toDate, setToDate] = useSessionState(getDefaultDateString(), 'toDate');
    const [gender, setGender] = useSessionState('', 'gender');
    const [birthDate, setBirthDate] = useSessionState('', 'birthDate');
    const [calendarYear, setCalendarYearInputDate] = useCalendarYear(null);
    const [oceServiceMode, setOceServiceMode] = useSessionState('', 'oceServiceMode');

    const navigate = useNavigate();

    const getNewDisanosisCode = () => {
        return {
            diagnosisCode: '',
        };
    };

    const getNewValueCode = () => {
        return {
            code: '',
            amount: ''
        };
    };

    const getNewConditionCode = () => {
        return {
            code: ''
        };
    };

    const getNewOccurrenceCode = () => {
        return {
            code: ''
        };
    };

    const DEFAULT_CONDITION_CODES = [getNewConditionCode(), getNewConditionCode(), getNewConditionCode(), getNewConditionCode(), getNewConditionCode()];
    const DEFAULT_VALUE_CODES = [getNewValueCode(), getNewValueCode(), getNewValueCode(), getNewValueCode(), getNewValueCode()];
    const DEFAULT_DIAGNOSIS_CODES = [getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode(), getNewDisanosisCode()];
    const DEFAULT_OCCURRENCE_CODES = [getNewOccurrenceCode(), getNewOccurrenceCode(), getNewOccurrenceCode(), getNewOccurrenceCode(), getNewOccurrenceCode()];

    const diagnosisesCollection = useCollection(DEFAULT_DIAGNOSIS_CODES, getNewDisanosisCode, 'diagnosisCodes');

    const getNewLine = (index) => {
        return {
            lineId: index + 1,
            revenueCode: '',
            procedureCode: '',
            modifierOne: '',
            modifierTwo: '',
            serviceDate: '',
            units: 0,
            billed: ''
        }
    };

    const valueCodeCollection = useCollection(DEFAULT_VALUE_CODES, getNewValueCode, 'valueCodes');
    const conditionCodeCollection = useCollection(DEFAULT_CONDITION_CODES, getNewConditionCode, 'conditionCodes');
    const occurrenceCodeCollection = useCollection(DEFAULT_OCCURRENCE_CODES, getNewOccurrenceCode, 'occurrenceCodes');


    const getDefaultLines = () => {
        return [getNewLine(0), getNewLine(1), getNewLine(2), getNewLine(3), getNewLine(4)];
    };

    const linesCollection = useCollection(getDefaultLines(), getNewLine, 'lines');

    const prepareLines = linesCollection => prepareServiceLines(linesCollection, line => line.billed);

    const oce = useOceApi();
    const pricer = enableAsyncPricingApi ? useAsyncPricerApi(modelType) : usePricerApi(modelType);

    const [fileExporting, _fileExportSuccess, fileExportError, fileExportExecute, fileExportReset] = useExportRequest(modelType, {
        "claimId": claimId,
        "providerName": providerName,
        "medicareId": medicareId,
        "npi": npi,
        "birthDate": birthDate,
        "gender": gender,
        "zipCode": zipCode,
        "placeOfService": placeOfService,
        "billType": billType,
        "fromDate": fromDate,
        "toDate": toDate,
        "diagnosisCodes": diagnosisesCollection.values,
        "lines": linesCollection.values,
        oceServiceMode,
        valueCodes: valueCodeCollection.values,
        conditionCodes: conditionCodeCollection.values,
        occurrenceCodes: occurrenceCodeCollection.values
    });

    const handleFileSave = () => {
        pricer.reset();
        oce.reset();
        fileExportExecute();
    };

    const [fileImporting, fileImportSuccess, fileImportError, fileImportResult, fileImportReset, fileImportOnChange] = useImportRequest(modelType);

    const handleFileImportOnChange = (e) => {
        pricer.reset();
        oce.reset();
        fileImportOnChange(e);
    };

    const handleSubmit = async () => {
        if (!enableOceEnhancements) {
            oce.reset();
        }

        fileExportReset();
        fileImportReset();

        if (enableAsyncPricingApi) {
            await pricer.execute(formatRequest());
        } else {
            await pricer.execute(calendarYear, formatRequest());
        }
    };

    const addOceResultToRequest = (request) => {
        if (!oce.result) return request;

        if (oce.result.claimEdits) {
            if (oce.result.claimEdits.processedFlag && isNotNullOrUndefined(oce.result.claimEdits.processedFlag.value)) {
                request.clmProcessedFlg = oce.result.claimEdits.processedFlag.value.toString();
            }

            if (oce.result.claimEdits.overallDispositionFlag && isNotNullOrUndefined(oce.result.claimEdits.overallDispositionFlag.value)) {
                request.clmDisposition = oce.result.claimEdits.overallDispositionFlag.value.toString();
            }
        }

        if (oce.result.lineEdits && oce.result.lineEdits.length > 0 && request.serviceLineItems && request.serviceLineItems.length > 0) {
            for (const serviceLine of request.serviceLineItems) {
                const lineEdit = oce.result.lineEdits.find(le => le.lineNumber === serviceLine.lineId);
                if (!lineEdit) continue;

                if (lineEdit.denialOrRejectionFlag && isNotNullOrUndefined(lineEdit.denialOrRejectionFlag.value)) {
                    serviceLine.lineDenialFlag = lineEdit.denialOrRejectionFlag.value.toString();
                }

                if (lineEdit.procedureCodeEdits && lineEdit.procedureCodeEdits.length > 0) {
                    if (lineEdit.procedureCodeEdits[0] && isNotNullOrUndefined(lineEdit.procedureCodeEdits[0].code)) {
                        serviceLine.proceEdit1 = lineEdit.procedureCodeEdits[0].code.toString().padStart(3, '0');
                    }
                    if (lineEdit.procedureCodeEdits[1] && isNotNullOrUndefined(lineEdit.procedureCodeEdits[1].code)) {
                        serviceLine.proceEdit2 = lineEdit.procedureCodeEdits[1].code.toString().padStart(3, '0');
                    }
                    if (lineEdit.procedureCodeEdits[2] && isNotNullOrUndefined(lineEdit.procedureCodeEdits[2].code)) {
                        serviceLine.proceEdit3 = lineEdit.procedureCodeEdits[2].code.toString().padStart(3, '0');
                    }
                    if (lineEdit.procedureCodeEdits[3] && isNotNullOrUndefined(lineEdit.procedureCodeEdits[3].code)) {
                        serviceLine.proceEdit4 = lineEdit.procedureCodeEdits[3].code.toString().padStart(3, '0');
                    }
                    if (lineEdit.procedureCodeEdits[4] && isNotNullOrUndefined(lineEdit.procedureCodeEdits[4].code)) {
                        serviceLine.proceEdit5 = lineEdit.procedureCodeEdits[4].code.toString().padStart(3, '0');
                    }
                    serviceLine.addtlEdits = lineEdit.procedureCodeEdits.filter((_pce, i) => i > 4).map(pce => pce.code.toString().padStart(3, '0')).join(',') || null;
                }
            }
        }
    };

    const formatRequest = () => {
        const pricerRequest = {
            claimId: claimId || null,
            providerName: providerName || null,
            medicareId: medicareId || null,
            zipCode: zipCode || null,
            fromServiceDate: formatDateStringIfMatches(fromDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            toServiceDate: formatDateStringIfMatches(toDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT),
            pos: placeOfService || null,
            billType: billType || null,
            birthDate: formatDateStringIfMatches(birthDate, DATE_STRING_FORMAT, API_DATE_STRING_FORMAT) || null,
            gender,
            oceServiceMode,
            includeCmsExtendedRvus: "Y",
            mue: "N",
            maxProcs: "Y",
            diagnosis: diagnosisesCollection.values.filter(dc => dc.diagnosisCode).map(dc => {
                return {
                    icdDiag: dc.diagnosisCode || null,
                    poa: dc.poa || null
                }
            }),
            serviceLineItems: prepareLines(linesCollection)
        };

        if (enableOceEnhancements) {
            addOceResultToRequest(pricerRequest);
        }

        return pricerRequest;
    }

    const handleReset = () => {
        setClaimId('');
        setMedicareId('');
        setProviderName('');
        setNpi('');
        setPlaceOfService('');
        setBirthDate('');
        setGender('');
        setZipCode('');
        setBillType('');
        setOceServiceMode(EXECUTE);
        setFromDate(getDefaultDateString());
        setToDate(getDefaultDateString());
        diagnosisesCollection.setValues(DEFAULT_DIAGNOSIS_CODES);
        linesCollection.setValues(getDefaultLines());
        pricer.reset();
        oce.reset();
        conditionCodeCollection.setValues(DEFAULT_CONDITION_CODES);
        valueCodeCollection.setValues(DEFAULT_VALUE_CODES);
        occurrenceCodeCollection.setValues(DEFAULT_OCCURRENCE_CODES);
        fileImportReset();
        fileExportReset();
    };

    const handleRunOce = () => {
        fileExportReset();
        fileImportReset();
        oce.execute({
            claimId,
            medicareId,
            npi,
            billType,
            gender,
            birthDate,
            fromDate,
            toDate,
            diagnosisCodes: [
                ...diagnosisesCollection.values
            ],
            occurrenceCodes: [
                ...occurrenceCodeCollection.values
            ],
            valueCodes: [
                ...valueCodeCollection.values
            ],
            conditionCodes: [
                ...conditionCodeCollection.values
            ],
            lines: [
                ...linesCollection.values
            ]
        });
    };

    useEffect(() => {
        if (fileImportSuccess === true) {
            const setters = {
                "claimId": setClaimId,
                "providerName": setProviderName,
                "medicareId": setMedicareId,
                "npi": setNpi,
                "birthDate": setBirthDate,
                "gender": setGender,
                "zipCode": setZipCode,
                "placeOfService": setPlaceOfService,
                "billType": setBillType,
                "fromDate": setFromDate,
                "toDate": setToDate,
                "diagnosisCodes": diagnosisesCollection.setValues,
                "lines": linesCollection.setValues,
                valueCodes: valueCodeCollection.setValues,
                conditionCodes: conditionCodeCollection.setValues,
                occurrenceCodes: occurrenceCodeCollection.setValues,
                oceServiceMode: setOceServiceMode
            };

            handleReset();
            mapToSetters(fileImportResult, setters);
        }
    }, [fileImportSuccess]);

    useEffect(() => {
        setCalendarYearInputDate(fromDate);
    }, [fromDate]);

    useEffect(() => {
        if (pricer.success) {
            navigate('result', {
                state: {
                    result: pricer.result,
                    calendarYear,
                    lines: prepareLines(linesCollection),
                    oceResult: oce.result,
                    oceServiceMode
                }
            });
        }
    }, [pricer.success]);

    const handleValidateZipCode = useCallback((v) => validateZipCode(v, calendarYear, "Note: 9 digit zip code detail is required to determine carrier locality within this zip code. Where available, the full 9 digit zip code from the provider's address will automatically be used instead of the zip code entered here."), [calendarYear]);

    return (
        <div className="py-2">
            <div className="row my-2 sticky top g-0 border-bottom align-items-center">
                <div className="col">
                    <div className="row justify-content-between align-items-center">
                        <div className="col-auto">
                            <h3>OPPS/ASC Pricer</h3>
                            <div className="fs-6">
                                Enter all claim information requested below. Please press SUBMIT when complete to calculate the prospective payment amount.
                            </div>
                        </div>
                        <div className="col-auto">
                            <BackButton />
                        </div>
                    </div>
                </div>
            </div>
            {(pricer.error || oce.error || fileImportError || fileExportError) && (<div>
                <ApiError error={pricer.error} />
                <ApiError error={oce.error} />
                {fileImportError ? <ErrorMessage errors={[{ message: fileImportError }]} /> : null}
                {fileExportError ? <ErrorMessage errors={[{ message: fileExportError }]} /> : null}
            </div>)}
            {oce.success && oce.result && <OCEResults result={oce.result} />}
            <form noValidate={true}>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input id="claim-id" labelClassName="fw-bold" label="Claim ID" onChange={setClaimId} value={claimId} />
                    </div>
                    <ProviderMapper inputs={[
                        {
                            name: "providerName",
                            label: "Provider Name",
                            superscript: "1",
                            value: providerName,
                            onChange: setProviderName
                        },
                        {
                            name: "medicareId",
                            label: "Medicare ID",
                            superscript: "1",
                            value: medicareId,
                            onChange: setMedicareId
                        },
                        {
                            name: "npi",
                            label: "NPI",
                            sublabel: "(facilities may have multiple NPIs)",
                            superscript: "1",
                            value: npi,
                            onChange: setNpi
                        },
                        {
                            name: "zipcode",
                            onChange: setZipCode,
                            isSubordinate: true,
                        }
                    ]} model={modelType} />
                </div>
                <div className="row my-2">
                    {enableOceEnhancements && (<><div className="col-md-3">
                        <Input label="Date of Birth" preservePlaceholder={true} id="date-of-birth" labelClassName="fw-bold" type="date" onChange={setBirthDate} value={birthDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                        <div className="col-md-3">
                            <Select label="Gender" id="gender" labelClassName="fw-bold" onChange={setGender} value={gender}>
                                {genderValues.map(gv => <option key={gv.value} value={gv.value}>{gv.text}</option>)}
                            </Select>
                        </div></>)}
                    <div className="col-md-3">
                        {enable5ZipWarning &&
                            (<ValidatedInput
                                label="ZIP Code"
                                maxLength={9}
                                id="zip-code"
                                labelClassName="fw-bold"
                                onChange={setZipCode}
                                value={zipCode}
                                onValidate={handleValidateZipCode}
                            />)}
                        {!enable5ZipWarning &&
                            (<Input
                                label="ZIP Code"
                                maxLength={9}
                                id="zip-code"
                                labelClassName="fw-bold"
                                onChange={setZipCode}
                                value={zipCode}
                            />)}
                    </div>
                    <div className="col-md-3">
                        <Input label="Place of Service" labelClassName="fw-bold" id="place-of-service" onChange={setPlaceOfService} value={placeOfService} />
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col-md-3">
                        <Input label="Bill Type" labelClassName="fw-bold" id="bill-type" onChange={setBillType} value={billType} />
                    </div>
                    <div className="col-md-3">
                        <Input label="From Date" preservePlaceholder={true} labelClassName="fw-bold" id="from-date" type="date" onChange={setFromDate} value={fromDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3">
                        <Input label="To Date" preservePlaceholder={true} labelClassName="fw-bold" id="to-date" type="date" onChange={setToDate} value={toDate} placeholder={DATE_STRING_FORMAT.toUpperCase()} />
                    </div>
                    <div className="col-md-3 align-self-center mt-4 fw-bold">
                        {calendarYear && <>Calendar Year {calendarYear}</>}
                    </div>
                </div>
                <div> <sup> 1 </sup> Pricing depends only on Medicare ID </div>
                {enableAsyncPricingApi &&
                    <div className="row my-2">
                        <div className="col-md-3">
                            <Select id="oce-service-mode" value={oceServiceMode} labelClassName="fw-bold" onChange={setOceServiceMode} label="Set OCE Execution">
                                {serviceModeValues.map((v, i) => <option key={i} value={v.value}>{v.text}</option>)}
                            </Select>
                        </div>
                    </div>}
                <hr />
                <div className="row my-2">
                    {enableOceEnhancements && <>
                        <div className="col-md-3">
                            <MultiInput id="value-codes"
                                label="Value Codes"
                                collection={valueCodeCollection}
                                lineCountLabel="Total Value Codes"
                                max={30}>
                                <MultiInputType label="Value Code"
                                    name="code"
                                    type="text"
                                    maxLength={2} />
                                <MultiInputType label="Amount"
                                    name="amount"
                                    type="text"
                                    maxLength={9}
                                    placeholder="000000.00" />
                            </MultiInput>
                        </div>
                        <div className="col-md-3">
                            <MultiInput id="condition-codes"
                                label="Condition Codes"
                                collection={conditionCodeCollection}
                                lineCountLabel="Total Condition Codes"
                                max={30}>
                                <MultiInputType label="Condition Code"
                                    name="code"
                                    type="text"
                                    maxLength={2} />
                            </MultiInput>
                        </div>
                        <div className="col-md-3">
                            <MultiInput id="occurrence-codes"
                                label="Occurrence Codes"
                                collection={occurrenceCodeCollection}
                                lineCountLabel="Total Occurrence Codes"
                                max={30}>
                                <MultiInputType label="Occurrence Code"
                                    name="code"
                                    type="text"
                                    maxLength={2} />
                            </MultiInput>
                        </div>
                    </>}

                    <div className="col-md-3">
                        <MultiInput id="diagnosis-codes"
                            note="Enter ICD codes without the decimal."
                            label="ICD-10 Diagnosis Codes"
                            subLabel="Diagnosis"
                            collection={diagnosisesCollection}
                            lineCountLabel="Total Diagnosis Codes"
                            max={28}>
                            <MultiInputType label="Diagnosis Code"
                                name="diagnosisCode"
                                type="text"
                                maxLength={7} />
                        </MultiInput>
                    </div>
                </div>
                <hr />
                <div className="row g-0">
                    <div className="col my-2">
                        <EntryTable id="service-line-items"
                            collection={linesCollection}
                            lineCountLabel="Total Lines"
                            disableMaxWarningMessage={true}
                            upDownNavigation={true}
                            max={100}
                            runningTotal={{
                                name: 'billed',
                                className: 'me-2',
                                formatter: formatStringAsCurrency
                            }}>
                            <EntryTableColumn label="Line ID"
                                name="lineId"
                                type="label"
                                className="mw-50" />
                            <EntryTableColumn label="Revenue Code"
                                name="revenueCode"
                                type="text" />
                            <EntryTableColumn label="Procedure Code (CPT/HCPCS)"
                                name="procedureCode"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Modifier 1"
                                name="modifierOne"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Modifier 2"
                                name="modifierTwo"
                                type="text"
                                valueTransform={transformStringToUpCase} />
                            <EntryTableColumn label="Service Date"
                                name="serviceDate"
                                type="date"
                                className="mw-125"
                                placeholder={DATE_STRING_FORMAT.toUpperCase()}
                                onValidate={(value) => validateLineServiceDate(value, fromDate, toDate)} />
                            <EntryTableColumn label="Units"
                                name="units"
                                type="number"
                                className="mw-75"
                                valueTransform={transformNumberToOneDecimalPlace} />
                            <EntryTableColumn label="Billed"
                                name="billed"
                                type="text"
                                className="mw-100 text-end"
                                onValidate={validateBilled} />
                        </EntryTable>
                    </div>
                </div>
                <div className="row justify-content-between my-2 sticky bottom g-0 border-top">
                    <div className="col-auto align-self-center">
                        <div className="row g-0">
                            <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="save-request-button"
                                    disabled={pricer.loading || oce.loading || fileImporting || fileExporting}
                                    onClick={handleFileSave}
                                    text="Save" />
                            </div>
                            <div className="col-auto ms-2">
                                <FilePickerButton className="btn-outline-secondary" id="load-request-button"
                                    disabled={pricer.loading || oce.loading || fileImporting || fileExporting}
                                    onChange={handleFileImportOnChange}
                                    loading={fileImporting}
                                    text="Load"
                                    accepts=".mrpreq" />
                            </div>
                        </div>
                    </div>
                    <div className="col-auto align-self-center">
                        <ErrorAlert visible={oce.error || pricer.error || fileImportError || fileExportError} />
                        <ResultAlert visible={oce.success && oce.result} />
                    </div>
                    <div className="col-auto align-self-center">
                        <div className="row g-0 justify-cotent-end">
                            <div className="col-auto">
                                <Button className="btn-outline-secondary" id="reset-button"
                                    onClick={handleReset}
                                    disabled={pricer.loading || oce.loading || fileImporting || fileExporting}
                                    text="Reset" />
                            </div>
                            {!enableAsyncPricingApi && <div className="col-auto ms-2">
                                <Button className="btn-outline-secondary" id="run-oce-button"
                                    onClick={handleRunOce}
                                    disabled={oce.loading || pricer.loading || fileImporting || fileExporting}
                                    text="Run OCE"
                                    loading={oce.loading} />
                            </div>}
                            <div className="col-auto ms-2">
                                <Button className="btn-primary" id="submit-button"
                                    onClick={handleSubmit}
                                    disabled={pricer.loading || oce.loading || fileImporting || fileExporting}
                                    loading={pricer.loading}
                                    text="Submit" />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
